import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errDetail } from "@agent/lib/api";
import { PostPreview, CharCounter } from "@agent/components/PostPreview";
import { PublishModal } from "@agent/components/PublishModal";
import { DraftInputForm } from "@agent/components/DraftInputForm";
import { DraftEditorPanel } from "@agent/components/DraftEditorPanel";
import { useDraftEditor } from "@agent/hooks/useDraftEditor";
import { usePublishFlow } from "@agent/hooks/usePublishFlow";

export default function Composer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);

  const {
    draft, setDraft, instruction, setInstruction, regenLoading, regenerate,
    blogGenLoading, generateBlog, saving, save, showBlog, setShowBlog,
  } = useDraftEditor(id, navigate);

  const {
    publishOpen, setPublishOpen, publishing, webStatus, publish, copyForLinkedin,
    linkedinReady, liConnected, liHasOrgId, websiteReady,
  } = usePublishFlow(draft, setDraft);

  const generate = async (payload) => {
    setGenerating(true);
    try {
      const { data } = await api.post("/generate", payload);
      navigate(`/agent/composer/${data.id}`);
      toast.success("Draft generated");
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setGenerating(false);
    }
  };

  const overLimit = (draft?.content?.length || 0) > 3000;
  const blogGenerated = !!draft?.blog_markdown;
  const canPublish = draft && !overLimit && (draft.status === "approved" ||
    (draft.status === "published" && (!draft.linkedin_post_id || !draft.blog_url)));

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 flex flex-col lg:flex-row gap-6">
      {/* LEFT: workspace */}
      <div className="w-full lg:w-[46%] space-y-5">
        {!draft && <DraftInputForm generating={generating} onGenerate={generate} />}

        {draft && (
          <DraftEditorPanel
            draft={draft} setDraft={setDraft} onNewDraft={() => navigate("/agent")} overLimit={overLimit}
            instruction={instruction} setInstruction={setInstruction} regenLoading={regenLoading} regenerate={regenerate}
            blogGenerated={blogGenerated} blogGenLoading={blogGenLoading} generateBlog={generateBlog}
            showBlog={showBlog} setShowBlog={setShowBlog}
            saving={saving} canPublish={canPublish} linkedinReady={linkedinReady} liConnected={liConnected} liHasOrgId={liHasOrgId}
            onCopyLinkedin={copyForLinkedin} onSave={() => save()} onApprove={() => save({ status: "approved" })}
            onOpenPublish={() => setPublishOpen(true)}
          />
        )}
      </div>

      {/* RIGHT: live preview */}
      <div className="flex-1 grid-bg rounded-2xl border border-slate-200 p-6 sm:p-8 flex flex-col items-center min-h-[70vh]">
        <div className="w-full max-w-[555px] mb-4 flex items-center justify-between">
          <span className="px-3 py-1 rounded-full bg-white border border-slate-200 text-[11px] tracking-[0.15em] uppercase text-slate-500 font-semibold shadow-sm">Live page preview</span>
          {draft && <CharCounter content={draft.content} />}
        </div>
        <PostPreview content={draft?.content} imageIds={draft?.image_ids} />
        {generating && (
          <p className="text-sm text-indigo-600 font-medium mt-6 cursor-blink" data-testid="generating-indicator">Agent writing</p>
        )}
      </div>

      <PublishModal open={publishOpen} onClose={() => setPublishOpen(false)} onConfirm={publish}
        draft={draft} publishing={publishing} linkedinReady={linkedinReady} liConnected={liConnected} liHasOrgId={liHasOrgId}
        websiteReady={websiteReady} blogGenerated={blogGenerated}
        websiteHost={webStatus?.base_url ? webStatus.base_url.replace(/^https?:\/\//, "") : ""} />
    </div>
  );
}
